import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiDownload, FiHelpCircle, FiSearch, FiSliders } from 'react-icons/fi';
import DealDeskToolbarContext from './DealDeskToolbarContext';
import './WorkspaceDocumentToolbar.css';

const allowedActions = new Set(['guide', 'printCurrent', 'printFull', 'exportSow', 'exportAllZip', 'phase-one', 'openSearch', 'mobileMenu', 'expandAll', 'focusMode', 'printDoc']);
export default function WorkspaceDocumentToolbar({ frame, active = true, loading, label = 'Document sections', children, menuItems }) {
  const host = useContext(DealDeskToolbarContext);
  const [navigation, setNavigation] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const menu = useRef(null);
  const send = (command, id) => {
    if (['guide', 'openSearch', 'mobileMenu'].includes(command)) frame.current?.focus();
    frame.current?.contentWindow?.postMessage({ type: 'sow:navigation-command', command, id }, '*');
  };
  useEffect(() => {
    if (loading) setNavigation(null);
    const receive = event => {
      if (event.source !== frame.current?.contentWindow || event.origin !== 'null' || event.data?.type !== 'sow:navigation-state') return;
      const data = event.data.data;
      if (!data || !Array.isArray(data.tabs) || !data.tabs.length || data.tabs.length > 24 || !Array.isArray(data.actions)
        || data.actions.length > allowedActions.size || new Set(data.tabs.map(tab => tab?.id)).size !== data.tabs.length
        || data.tabs.some(tab => !/^tab-\d+$/.test(tab?.id || '') || typeof tab.label !== 'string' || !tab.label || tab.label.length > 80 || typeof tab.selected !== 'boolean')
        || data.actions.some(action => !allowedActions.has(action?.id) || typeof action.label !== 'string' || action.label.length > 80)) return;
      setNavigation(data);
      if (host) event.source.postMessage({ type: 'sow:navigation-attached' }, '*');
    };
    window.addEventListener('message', receive);
    frame.current?.contentWindow?.postMessage({ type: 'sow:navigation-request' }, '*');
    return () => window.removeEventListener('message', receive);
  }, [frame, loading, host]);
  useEffect(() => {
    setExportOpen(false);
  }, [active]);
  useEffect(() => {
    if (!exportOpen) return undefined;
    const close = event => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && menu.current?.contains(event.target)) return;
      setExportOpen(false);
      if (event.key === 'Escape') menu.current?.querySelector('button')?.focus();
    };
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', close);
    window.addEventListener('blur', close);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close); window.removeEventListener('blur', close); };
  }, [exportOpen]);
  if (!active) return null;
  if (!host) return children ? <div className="workspace-document-tools">{children}{menuItems}</div> : null;
  const exports = navigation?.actions.filter(action => !['guide','openSearch'].includes(action.id)) || [];
  const isDocumentation = navigation?.actions.some(action => action.id === 'openSearch');
  const tabsKeyDown = event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...event.currentTarget.querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[next]?.focus(); tabs[next]?.click(); tabs[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  return createPortal(<div className="workspace-document-tools" onKeyDown={event => {
    if (isDocumentation && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault(); event.stopPropagation(); send('openSearch');
    }
  }}>
    {children}
    {navigation ? <>
      <div className="workspace-document-tools__tabs" role="tablist" aria-label={label} onKeyDown={tabsKeyDown}>
        {navigation.tabs.map((tab, index) => <button key={tab.id} type="button" role="tab" aria-selected={tab.selected} aria-label={tab.label} title={tab.label}
          tabIndex={tab.selected || (!navigation.tabs.some(item => item.selected) && index === 0) ? 0 : -1}
          onClick={() => send('tab', tab.id)}>{isDocumentation ? <><span className="workspace-document-tools__wide-label">{tab.label}</span><span className="workspace-document-tools__short-label">{index === 0 ? 'Guide' : 'Atlas'}</span></> : tab.label}</button>)}
      </div>
      {navigation.actions.some(action => action.id === 'guide') && <button type="button" className="deal-desk__icon" title="SOW guide" aria-label="SOW guide" onClick={() => send('guide')}><FiHelpCircle /></button>}
      {navigation.actions.filter(action => action.id === 'openSearch').map(action => <button key={action.id} type="button" className="deal-desk__icon" title={action.label} aria-label={action.label} onClick={() => send(action.id)}><FiSearch /></button>)}
      {exports.length === 1 ? <button type="button" className="deal-desk__icon" title={exports[0].label} aria-label={exports[0].label} onClick={() => send(exports[0].id)}><FiDownload /></button>
        : exports.length > 1 && <div className="workspace-document-tools__export" ref={menu}>
          <button type="button" className={isDocumentation ? 'deal-desk__icon' : 'deal-desk__button'} aria-expanded={exportOpen} aria-label={isDocumentation ? 'Document tools' : 'Export document'} title={isDocumentation ? 'Document tools' : 'Export document'} onClick={() => setExportOpen(value => !value)}>{isDocumentation ? <FiSliders /> : <><FiDownload />Export</>}</button>
          {exportOpen && <div className="workspace-document-tools__menu" aria-label="Document export options">
            {exports.map(action => <button key={action.id} type="button" disabled={action.disabled} aria-pressed={action.id === 'focusMode' ? action.pressed : undefined} onClick={() => { send(action.id); setExportOpen(false); }}>{action.label}</button>)}
            {menuItems}
          </div>}
        </div>}
    </> : <span className="workspace-document-tools__loading" role="status">Opening document...</span>}
  </div>, host);
}
